import React, { useState, useEffect, useContext } from 'react'
import { Modal } from './modal'
import { StatusData, dollars, dollarsToNum, InstallerPatchableFields, Partiable, Adders, MimeTypes, LoanDocument, SolarDocStatus, LOAN_MODIFICATION_FEE, ChangeOrder } from '@oneethos/shared'
import { SystemDetailInputs } from './system-detail-inputs'
import { ImageUploader } from './image-uploader'
import { EquipmentOptions } from '@oneethos/shared'
import api from '../api-client'
import { toast } from 'react-toastify'
import { errorToString } from '@oneethos/shared'
import { Spinner } from '@fluentui/react'
import './contract-issue-resolver-modal.scss'
import { Checkbox, MinLengthTextarea } from './forms'
import { useAppState } from '../hooks'
import { selectChecklistItemsByLabel } from '../selectors/checklistItems'
import { AppContext } from '../appContext'

type ContractIssueResolverModalProps = {
  project: StatusData
  onClose: () => void
  onChange: (project: StatusData) => void
}

type WorkflowStep = 'post-ntp-fee' | 'instructions' | 'system-details' | 'contract-upload' | 'submit'

type PostNTPFeeStepProps = {
  handleNext: () => void
  changeOrder: Partial<ChangeOrder>
  setChangeOrder: (changeOrder: Partial<ChangeOrder>) => void
}

type ContractUploadStepProps = {
  existingContract: LoanDocument
  contract: LoanDocument
  setContract: (contract: LoanDocument) => void
  setContractChanged: (changed: boolean) => void
  handleBack: () => void
  handleNext: () => void
  systemChanged: boolean
  contractChanged: boolean
}

const PostNTPFeeStep = ({
  handleNext,
  changeOrder,
  setChangeOrder,
}: PostNTPFeeStepProps) => {
  const { tenant: { config: { vendorAchFormUrl } } } = useAppState()
  const [notesValid, setNotesValid] = useState(false)

  return <div className="contract-workflow-step post-ntp-fee-step">
    <h4 className="mb-4">Post-NTP Change Order</h4>

    <div className="d-flex flex-column text-align-center gap-2" style={{ margin: '0 auto' }}>
      <div className="form-group">
        <label className="label text-start">Reason for Changes</label>
        <MinLengthTextarea
          rows={3}
          value={changeOrder.installerNotes}
          onChange={(e) => setChangeOrder({ ...changeOrder, installerNotes: e.target.value })}
          onValidChange={setNotesValid}
          minLength={10}
          prompt="Please provide a more detailed explanation of the changes"
        />
      </div>

      <div className="alert alert-warning mb-4">
        Change orders submitted after NTP will incur a {dollars(LOAN_MODIFICATION_FEE)} loan modification fee.
        This is rolled into the amount financed unless paid by the installer.
      </div>
    </div>

    <div className="mb-4">
      <select className="form-select form-select-lg"
        value={changeOrder.feeCoveredBy}
        onChange={(e) => setChangeOrder({ ...changeOrder, feeCoveredBy: e.target.value as 'installer' | 'consumer' })}>
        <option value="installer">Installer will cover fee</option>
        <option value="consumer">Consumer will cover fee</option>
      </select>
    </div>

    {changeOrder.feeCoveredBy === 'installer' && (
      <div className="mb-4">
        <p className="mb-3">
          To cover the fee, please upload a completed copy of the {vendorAchFormUrl ? <a href={vendorAchFormUrl} target="_blank" rel="noopener noreferrer">Vendor ACH Form</a> : 'Vendor ACH Form'}.
        </p>
        <div className="text-center">
          <ImageUploader
            onChange={(f, status) => {
              setChangeOrder({
                ...changeOrder,
                vendorAchDocument: {
                  ...f,
                  docType: 'Vendor ACH Form',
                  status: status || SolarDocStatus.PendingUpload,
                  agentResult: undefined
                }
              })
            }}
            doc={changeOrder.vendorAchDocument}
            onRemove={() => setChangeOrder({ ...changeOrder, vendorAchDocument: undefined })}
            acceptContentTypes={MimeTypes.allowedUploadExtensions().join(',')}
          />
        </div>
      </div>
    )}

    <div className="buttons text-center mt-4">
      <button
        className="btn btn-primary btn-lg"
        onClick={handleNext}
        disabled={
          (changeOrder.feeCoveredBy === 'installer' && !changeOrder.vendorAchDocument) ||
          !notesValid
        }>
        Continue
      </button>
    </div>
  </div>
}

type SystemDetailsStepProps = {
  handleNext: () => void
  handleBack: () => void
  project: StatusData
  changeOrder: Partial<ChangeOrder>
  setChangeOrder: (changeOrder: Partial<ChangeOrder>) => void
  setSystemChanged: (systemChanged: boolean) => void
}

const SystemDetailsStep = ({
  handleNext, handleBack, project, changeOrder, setChangeOrder, setSystemChanged
}: SystemDetailsStepProps) => {
  return (
    <div className="contract-workflow-step">
      <h4 className="mb-4">Confirm System Details Match Contract</h4>
      <p className="mb-3">
        Please review the System Details below and make any necessary adjustments
        to ensure they match the signed contract exactly.
      </p>
      <div className="system-details-container">
        <SystemDetailInputs
          formData={changeOrder}
          disabled={false}
          selectedEquipment={EquipmentOptions.find(e => e.id === project.equipmentType)}
          isResidential={project.projectType === 'residential'}
          showValidation={true}
          handleChange={data => {
            setChangeOrder({ ...changeOrder, ...data })
            setSystemChanged(true)
          }}
          actionButtons={({ isValid }) => (
            <div className="buttons text-center mt-4">
              <button
                className="btn btn-secondary me-2"
                onClick={handleBack}
              >Back</button>
              <button
                className="btn btn-primary"
                onClick={handleNext}
                disabled={!isValid}
              >System Details are Accurate</button>
            </div>
          )}
        />
      </div>
    </div>
  )
}

const ContractUploadStep = ({
  existingContract,
  contract,
  setContract,
  setContractChanged,
  handleBack,
  handleNext,
  systemChanged,
  contractChanged
}: ContractUploadStepProps) => {
  const [needsNewContract, setNeedsNewContract] = useState(false)
  const { comments } = existingContract || {}

  if (existingContract && systemChanged && !needsNewContract && !contractChanged) {
    return <div className="contract-workflow-step">
      <div className="d-flex flex-column gap-2" style={{ maxWidth: '300px', margin: '0 auto', paddingTop: '5em' }}>
        <button
          className="btn btn-outline-primary"
          onClick={() => {
            // Re-initialize the contract as if it's new
            setContract({
              ...existingContract,
              comments: undefined,
              status: SolarDocStatus.Uploaded,
              agentResult: undefined
            })
            setContractChanged(true)
            handleNext()
          }}
        >
          Existing contract is accurate
        </button>
        <button
          className="btn btn-primary"
          onClick={() => {
            setNeedsNewContract(true)
            setContract(undefined)
          }}
        >
          I need to upload a new contract
        </button>
        <button
          className="btn btn-secondary me-2"
          onClick={handleBack}
        >
          Back
        </button>
      </div>
    </div>
  }

  return <div className="contract-workflow-step">
    <h4 className="mb-4">Upload Signed Contract</h4>
    <p className="mb-3">
      Please review the contract before uploading to confirm it
      matches the System Details exactly.
    </p>
    {!existingContract ? (
      comments ? (
        <div className="alert alert-warning mb-3">
          <strong>Contract Has Issues:</strong> The existing contract has been flagged with comments.
          You can either upload a new contract or confirm that the current contract details are correct.
        </div>
      ) : null) : null}

    <div className="contract-upload-section">
      <ImageUploader
        onChange={(f, status) => {
          setContract({
            ...f,
            docType: 'Signed Contract',
            status: status || SolarDocStatus.PendingUpload,
            agentResult: undefined
          })
          setContractChanged(true)
        }}
        doc={contract}
        onRemove={() => {
          setContract(undefined)
          setContractChanged(true)
        }}
        acceptContentTypes={MimeTypes.allowedUploadExtensions().join(',')}
      // logoPreviewStyles={{ height: '200px', width: '100%' }}
      />
    </div>

    {!systemChanged && !contractChanged ? (
      <div className="alert alert-secondary">
        You must make changes to either the System Details or the contract to continue.
      </div>
    ) : null}
    <div className="text-center mt-4">
      <button
        className="btn btn-secondary me-2"
        onClick={handleBack}
      >Back</button>
      <button
        className="btn btn-primary"
        onClick={handleNext}
        disabled={!contract || (needsNewContract && !contractChanged) || (!systemChanged && !contractChanged)}
      >Continue</button>
    </div>
  </div>
}

const InstructionsStep = ({ handleNext, handleBack, project }) => {
  const [acks, setAcks] = useState({
    signed: false,
    itemized: false,
    amountsMatch: false,
    sizeMatches: false,
    excludesBankFees: false,
    includesDownPayment: false,
    nameMatches: false,
    addressMatches: false
  })

  // Secret feature: Press 'x' to check all boxes
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'x' || event.key === 'X') {
        setAcks({
          signed: true,
          itemized: true,
          amountsMatch: true,
          sizeMatches: true,
          excludesBankFees: true,
          includesDownPayment: true,
          nameMatches: true,
          addressMatches: true
        })
      }
    }

    document.addEventListener('keydown', handleKeyPress)

    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  return <>
    <h4>Contract Instructions</h4>
    <ul className="instructions form-group">
      <li className="mb-3">
        <Checkbox
          label="Signed by both installer and customer"
          value={acks.signed}
          onChange={() => setAcks({ ...acks, signed: !acks.signed })}
        />
      </li>
      <li className="mb-3">
        <Checkbox
          label={project.customerName ? `Name listed as: ${project.customerName}` : 'Customer name matches their ID'}
          value={acks.nameMatches}
          onChange={() => setAcks({ ...acks, nameMatches: !acks.nameMatches })}
        />
      </li>
      <li className="mb-3">
        <Checkbox
          label={`Address is: ${project.address}`}
          value={acks.addressMatches}
          onChange={() => setAcks({ ...acks, addressMatches: !acks.addressMatches })}
        />
      </li>
      <li className="mb-3">
        <Checkbox
          label="Contract is itemized"
          value={acks.itemized}
          onChange={() => setAcks({ ...acks, itemized: !acks.itemized })}
        />
      </li>
      <li className="mb-3">
        <Checkbox
          label="Amounts match System Details"
          value={acks.amountsMatch}
          onChange={() => setAcks({ ...acks, amountsMatch: !acks.amountsMatch })}
        />
      </li>
      <li className="mb-3">
        <Checkbox
          label="System Size matches exactly (no rounding)"
          value={acks.sizeMatches}
          onChange={() => setAcks({ ...acks, sizeMatches: !acks.sizeMatches })}
        />
      </li>
      <li className="mb-3">
        <Checkbox
          label="Does not include bank fees"
          value={acks.excludesBankFees}
          onChange={() => setAcks({ ...acks, excludesBankFees: !acks.excludesBankFees })}
        />
      </li>
      <li className="mb-3">
        <Checkbox
          label="Includes down payment (if applicable)"
          value={acks.includesDownPayment}
          onChange={() => setAcks({ ...acks, includesDownPayment: !acks.includesDownPayment })}
        />
      </li>
    </ul>
    <div className="text-center mt-4">
      {project.nTPGiven ? <button
        className="btn btn-secondary me-2"
        onClick={handleBack}
      >
        Back
      </button> : null}
      <button
        className="btn btn-primary btn-lg"
        onClick={handleNext}
        disabled={Object.values(acks).some(a => !a)}
      >
        Next
      </button>
    </div>
  </>
}


export const ContractIssueResolverModal = ({
  project,
  onClose,
  onChange
}: ContractIssueResolverModalProps) => {
  const existingContract = StatusData.docForSlot(project, 'Signed Contract')
  const isPostNTP = !!project.nTPGiven
  const initFormData = Partiable.limit(project.toFormData(), InstallerPatchableFields)
  const { state: { checklistItemsConfig } } = useContext(AppContext)
  const contractChecklistItem = selectChecklistItemsByLabel(checklistItemsConfig, 'Signed Contract')

  const [changeOrder, setChangeOrder] = useState<Partial<ChangeOrder>>({
    ...initFormData,
    feeCoveredBy: 'installer',
    installerNotes: ''
  })
  const [currentStep, setCurrentStep] = useState<WorkflowStep>(isPostNTP ? 'post-ntp-fee' : 'instructions')
  const [systemChanged, setSystemChanged] = useState(false)
  const [contract, setContract] = useState<LoanDocument>(existingContract)
  const [contractChanged, setContractChanged] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleBack = () => {
    switch (currentStep) {
      case 'post-ntp-fee':
        // Can't go back from first step
        break
      case 'instructions':
        setCurrentStep(isPostNTP ? 'post-ntp-fee' : 'instructions')
        break
      case 'system-details':
        setCurrentStep('instructions')
        break
      case 'contract-upload':
        setCurrentStep('system-details')
        break
      case 'submit':
        setCurrentStep('contract-upload')
        break
      default:
        break
    }
  }

  const handleNext = () => {
    switch (currentStep) {
      case 'post-ntp-fee':
        setCurrentStep('instructions')
        break
      case 'instructions':
        setCurrentStep('system-details')
        break
      case 'system-details':
        setCurrentStep('contract-upload')
        break
      case 'contract-upload':
        setCurrentStep('submit')
        break
      default:
        break
    }
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)

      // Add system details if changed
      let changeOrderResponse
      if (systemChanged) {
        const adders = new Adders(changeOrder.adders)

        Object.assign(changeOrder, {
          adders: adders.getList(),
          solarCost: dollarsToNum(changeOrder.solarCost)
        })

        changeOrderResponse = await api.post(`/loanapps/${project.id}/change-orders`, changeOrder)
      }

      // Add contract if changed
      if (contractChanged && contract) {
        await api.put(`/loanapps/${project.id}/docs/submit`, {
          documents: [contract],
          uploadType: 'solar_contract',
          notes: 'Contract has been updated',
          checklistItemId: contractChecklistItem.id
        })
      }

      // Handle different response types based on whether app has been submitted
      if (changeOrderResponse?.lapp) {
        // Application hasn't been submitted - changes applied directly
        toast.success('Changes submitted successfully and customer has been notified.')
      } else if (changeOrderResponse?.changeOrder) {
        toast.success('Change order has been submitted for customer review.')
      }

      // Refresh project data
      const updatedProject = await api.getProject(project.id)
      onChange(updatedProject)
      setSubmitted(true)

      toast.success(updatedProject.activeAgentRuns().length > 0
        ? '🎉 Your documents have been received and are being automatically reviewed, stand by for an update momentarily...'
        : 'Documents uploaded successfully, you will be notified once they are reviewed', { autoClose: false })
      // Close modal after a brief delay
      setTimeout(() => { onClose() }, 2000)
    } catch (ex) {
      toast.error(errorToString(ex), { autoClose: false })
    } finally {
      setSubmitting(false)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'post-ntp-fee':
        return (
          <PostNTPFeeStep
            handleNext={handleNext}
            changeOrder={changeOrder}
            setChangeOrder={setChangeOrder}
          />
        )

      case 'instructions':
        return (
          <div className="contract-workflow-step">
            <InstructionsStep handleNext={handleNext} handleBack={handleBack} project={project} />
          </div>
        )

      case 'system-details':
        return <SystemDetailsStep
          handleNext={handleNext}
          handleBack={handleBack}
          project={project}
          changeOrder={changeOrder}
          setChangeOrder={setChangeOrder}
          setSystemChanged={setSystemChanged}
        />

      case 'contract-upload':
        return <ContractUploadStep
          existingContract={existingContract}
          contract={contract}
          setContract={setContract}
          setContractChanged={setContractChanged}
          handleBack={handleBack}
          handleNext={handleNext}
          systemChanged={systemChanged}
          contractChanged={contractChanged}
        />

      case 'submit':
        return (
          <div className="contract-workflow-step">
            <h4 className="mb-4">Submit Changes</h4>

            <div className="alert alert-info mb-3">
              By submitting these changes, you acknowledge that any discrepancies between the
              contract and System Details are likely to result in processing delays.
            </div>

            <div className="alert alert-info mb-3">
              {project.installersStatus === 'Awaiting Application' ? (
                <>
                  Submitting changes before the customer has submitted their application will
                  send a notification and prompt them to complete their application.
                </>
              ) : (
                <>
                  Submitting changes after the customer has submitted their application will
                  send a notification and prompt them to review and sign new loan documents.
                </>
              )}
            </div>
            {!submitted ? (
              <div className="text-center">
                <button
                  className="btn btn-secondary me-2"
                  onClick={handleBack}
                >
                  Back
                </button>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? <Spinner /> : 'Submit & Notify Customer'}
                </button>
              </div>
            ) : (
              <div className="alert alert-success mt-3">
                <strong>Success!</strong> Your updates have been submitted successfully.
              </div>
            )}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <Modal
      onClose={onClose}
      containerStyles={{ maxWidth: '1000px' }}
      bodyStyles={{ maxHeight: 'none' }}
      dismissable={true}
      hideCloseButton={true}
    >
      <div className="contract-workflow">
        {/* Progress indicator */}
        <div className="workflow-progress mb-4">
          <div className="progress-steps">
            {(isPostNTP
              ? ['post-ntp-fee', 'instructions', 'system-details', 'contract-upload', 'submit']
              : ['instructions', 'system-details', 'contract-upload', 'submit']
            ).map((step, index) => {
              const allSteps = isPostNTP
                ? ['post-ntp-fee', 'instructions', 'system-details', 'contract-upload', 'submit']
                : ['instructions', 'system-details', 'contract-upload', 'submit']
              const currentIndex = allSteps.indexOf(currentStep)

              return (
                <div
                  key={step}
                  className={`progress-step ${currentStep === step ? 'active' : ''} ${index < currentIndex ? 'completed' : ''}`}
                  onClick={() => index < currentIndex ? setCurrentStep(step as WorkflowStep) : undefined}
                >
                  <div className="step-number">{index + 1}</div>
                  <div className="step-label">
                    {step === 'post-ntp-fee' && 'Post-NTP Fee'}
                    {step === 'instructions' && 'Instructions'}
                    {step === 'system-details' && 'System Details'}
                    {step === 'contract-upload' && 'Contract Upload'}
                    {step === 'submit' && 'Submit'}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Step content */}
        {renderStepContent()}
      </div>
    </Modal>
  )
}

export default ContractIssueResolverModal
